import React, { useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { ButtonItem } from '@atlaskit/menu';
import { Flex } from '@atlaskit/primitives';

import { ExternalUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozenge';

import { getParticipantColors, getCurrentUserColors } from '../presenceUtils';
import type { ContentMode, Participant } from '../presenceTypes';
import { useScrollToTelepointer } from '../useScrollToTelepointer';

import { PresenceAvatar } from './PresenceAvatar';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown as a title of a section inside of a menu. The people in the section are editing a page',
	},
	viewing: {
		id: 'team-presence.menu.viewing',
		defaultMessage: 'Viewing',
		description: 'The text is used to indicate that the current user is `Viewing the page`',
	},
	you: {
		id: 'team-presence.menu.current-user.marker',
		defaultMessage: 'You',
		description: 'The text is used to indicate that the current user is `You`',
	},
	clickToFind: {
		id: 'team-presence.menu-item.click-to-find',
		defaultMessage: 'Click to find',
		description:
			"The text is used in the avatar tooltip to indicate that the avatar can be clicked to locate the user's pointer in the document. When clicked, the editor scrolls to that pointer's position.",
	},
});

const DESCRIPTION_SEPARATOR = ' - ';

export type PresenceMenuItemProps = {
	participant: Participant;
	contentMode: ContentMode;
	contentId: string;
	contentType: string;
	isCurrentUser?: boolean;
};

export const PresenceMenuItem = ({
	participant,
	contentMode,
	contentId,
	contentType,
	isCurrentUser,
}: PresenceMenuItemProps) => {
	const { formatMessage } = useIntl();

	const activityText =
		participant.activity === 'editor' ? formatMessage(i18n.editing) : formatMessage(i18n.viewing);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isEligibleToScroll, scrollToTelepointer } = useScrollToTelepointer({
		contentType,
		contentMode,
		// TODO: https://product-fabric.atlassian.net/browse/MDS-1320
		participant: participant as any,
	});

	const handleClick = useCallback(
		(currentParticipant: Participant) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					product: 'confluence',
					source: 'presenceMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceMenuItem',
					attributes: {
						contentId,
						contentType,
						contentMode,
					},
				},
			}).fire();

			scrollToTelepointer();
			currentParticipant.onClick?.();
		},
		[createAnalyticsEvent, contentId, contentType, contentMode, scrollToTelepointer],
	);

	const isAnonymous = participant.userId === 'unidentified';

	const { id } = participant;

	return (
		<ButtonItem
			onClick={() => handleClick(participant)}
			testId="team-presence-menu-item"
			data-vc="team-presence-menu-item"
			iconBefore={
				<PresenceAvatar
					name={participant.name}
					src={participant.src}
					testId="team-presence-menu-item-avatar"
					badgeTextColor={
						isCurrentUser ? getCurrentUserColors().textColor : getParticipantColors(id).textColor
					}
					borderColor={
						isCurrentUser
							? getCurrentUserColors().backgroundColor
							: getParticipantColors(id).backgroundColor
					}
					activity={participant.activity ?? 'viewer'}
					contentMode={contentMode}
					contentId={contentId}
					contentType={contentType}
					isEligibleToScroll={isEligibleToScroll}
				/>
			}
			description={
				<>
					{activityText}
					{isEligibleToScroll && (
						<>
							{DESCRIPTION_SEPARATOR}
							<FormattedMessage {...i18n.clickToFind} />
						</>
					)}
				</>
			}
		>
			<Flex alignItems="baseline" gap="space.050">
				{participant.name}
				{participant.isGuest && <ExternalUserLozenge />}
				{isCurrentUser && isAnonymous && (
					<Lozenge appearance="inprogress">{formatMessage(i18n.you)}</Lozenge>
				)}
			</Flex>
		</ButtonItem>
	);
};
